/**
 * `refetchRealtimeQuery(qc, queryKey)` — fire a real HTTP refetch on a
 * realtime query, bypassing the subscriber-sentinel last-setOptions-wins
 * problem.
 *
 * **The problem this exists to solve.**
 *
 * Calling `queryClient.refetchQueries({ queryKey })` for a Morfi
 * realtime query LOOKS correct but is broken in practice:
 *
 *   - TanStack stores ONE `queryFn` per `Query`. The LAST observer
 *     that called `setOptions` wins. The SDK's `<Items>`, `useListIds`,
 *     and per-field hooks register subscriber observers (enabled:false,
 *     queryFn = the subscriber sentinel) on the SAME `queryKey` as the
 *     parent. Subscribers always mount AFTER the parent (parent
 *     renders first, leaves render and attach their own observers), so
 *     `Query.options.queryFn` typically points at the sentinel by the
 *     time anyone wants to refetch.
 *
 *   - `qc.refetchQueries({ queryKey })` calls `Query.fetch()` without
 *     passing an options.queryFn, so it uses whatever is latched on
 *     the Query — the sentinel. The sentinel resolves with cached
 *     data (NOT rejects, post the recent fix), so TanStack's success
 *     path runs but the resolved data is identical to what's already
 *     there. **No HTTP fires.** The cache stays stale.
 *
 * **The fix.** `QueryObserver.refetch()` calls
 * `currentQuery.fetch(this.options, fetchOptions)` — passing THE
 * OBSERVER'S own options, which Query.fetch then latches into the
 * Query before running. So if we find the parent (data-owning)
 * observer and call ITS `refetch()`, the real queryFn fires and the
 * HTTP request actually goes out.
 *
 * Iterate the Query's observers, skip any whose queryFn is the
 * subscriber sentinel, and refetch through the first remaining one.
 * One `obs.refetch()` per Query is enough — TanStack's in-flight
 * promise sharing dedupes any additional refetch calls.
 *
 * **Use everywhere** you'd otherwise reach for
 * `qc.refetchQueries({ queryKey })` on a Morfi realtime query: the
 * `<SdkScreen>` reacquire flow, `useSdkScreenRefresh`'s pull-to-refresh,
 * connectivity reconnect fan-out, sandbox bridge commands. The helper
 * is the canonical refetch path for realtime queries.
 *
 * Returns a Promise that resolves when the parent's refetch settles.
 * If no parent observer is found (every observer is a subscriber, or
 * the Query has no observers), resolves immediately to `undefined`
 * without firing anything — a missing parent means nobody's mounted
 * to receive the data anyway.
 */

import type {
  Query,
  QueryClient,
  QueryFunction,
  QueryKey,
  QueryObserverResult,
} from '@tanstack/react-query';
import { isNonRetryableSdkError } from '../../errors/policy/retry-policy.js';
import { pollingBlocked, pollingState } from '../polling/state.js';
import { getCachedQuery } from './get-cached-query.js';
import { isSubscriberQueryFn } from './subscriber-query-fn.js';

interface RefetchableObserver {
  readonly options: {
    readonly enabled?: boolean;
    readonly queryFn?: QueryFunction;
  };
  refetch(options?: {
    readonly cancelRefetch?: boolean;
  }): Promise<QueryObserverResult<unknown, Error>>;
}

export type RealtimeRefetchOutcome = 'success' | 'error' | 'no_observer';

// `cancelRefetch: false` is load-bearing. TanStack's `observer.refetch()`
// defaults `cancelRefetch` to TRUE (query-core `queryObserver` →
// `Query.fetch`): if the query already holds data and a fetch is in flight,
// it ABORTS that fetch (the AbortSignal fires → the request shows
// `(canceled)` in the network panel) and starts a fresh one. These
// realtime/reconnect catch-up refetches fire on every connectivity
// transition, NATS event, and subject reacquire — so on a busy domain
// (e.g. the markets feed) they repeatedly abort each other and the
// in-flight feed fetch, starving the list. A catch-up refetch only wants
// FRESH DATA, and an already-running fetch IS fresh data, so `false` makes
// TanStack dedupe onto the in-flight promise instead of cancel-and-restart.
const NO_CANCEL: { readonly cancelRefetch: false } = { cancelRefetch: false };

export async function refetchRealtimeQuery(
  queryClient: QueryClient,
  queryKey: QueryKey,
  preferredQueryFn?: QueryFunction,
): Promise<RealtimeRefetchOutcome> {
  const query = getCachedQuery(queryClient, { queryKey });
  if (query === undefined) return 'no_observer';
  if (pollingState(query).cooldownUntil > Date.now()) return 'error';
  if (query.meta?.pollingManaged === true && pollingBlocked(query)) return 'error';
  // A query sitting in a deterministic (zero-retry-budget) error state —
  // validation, schema, precondition — returns the SAME error on every
  // attempt; a realtime nudge cannot fix it. Refetching it here would turn
  // broadcast invalidation subjects (per-trade events) into a request storm
  // against a permanently-failing endpoint. New args mint a new queryKey, so
  // a corrected caller is unaffected by this skip.
  if (query.state.status === 'error' && isNonRetryableSdkError(query.state.error)) {
    return 'error';
  }
  const eligible: RefetchableObserver[] = [];
  for (const obs of query.observers) {
    if (isSubscriberQueryFn(obs.options.queryFn)) continue;
    const refetchable = obs as unknown as RefetchableObserver;
    if (refetchable.options.enabled === false) continue;
    eligible.push(refetchable);
  }
  const selected =
    preferredQueryFn === undefined
      ? eligible[0]
      : (eligible.find((observer) => observer.options.queryFn === preferredQueryFn) ?? eligible[0]);
  if (selected === undefined) return 'no_observer';
  await selected.refetch(NO_CANCEL);
  // `select` failures are observer-local and must not turn a successful
  // shared-cache fetch into a transport retry loop. The Query owns the raw
  // HTTP result, so its state is the authoritative refetch outcome.
  return query.state.status === 'error' ? 'error' : 'success';
}

/**
 * Variant that refetches EVERY currently-active realtime query by
 * iterating the QueryCache. Used by `useSdkScreenRefresh` for the
 * pull-to-refresh gesture and by the connectivity fan-out for the
 * post-reconnect catch-up. "Active" means the Query has at least one
 * mounted observer (TanStack's standard definition) — refetching
 * unmounted queries has no UI effect anyway since no observer is
 * listening.
 *
 * `predicate` filters which active queries to refetch. Default is
 * "every active query"; the connectivity fan-out passes a predicate
 * that matches `meta.realtime === true` so non-SDK queries the
 * consumer mounted alongside ours stay untouched.
 *
 * Returns immediately — observer refetches fire in parallel; the
 * `useIsFetching` hook is the source of truth for any spinner.
 */
export function refetchAllActiveRealtimeQueries(
  queryClient: QueryClient,
  predicate?: (query: Query) => boolean,
): void {
  const queryCache = queryClient.getQueryCache();
  for (const query of queryCache.getAll()) {
    if (query.observers.length === 0) continue;
    if (pollingState(query).cooldownUntil > Date.now()) continue;
    if (query.meta?.pollingManaged === true && pollingBlocked(query)) continue;
    // A query still doing its first fetch (cold load) has nothing to catch
    // up — the in-flight fetch already returns the current state. Skipping
    // it avoids piling a redundant refetch onto the cold load during the
    // reconnect fan-out's burst.
    if (query.state.status === 'pending') continue;
    // Same deterministic-error skip as the single-query path above.
    if (query.state.status === 'error' && isNonRetryableSdkError(query.state.error)) continue;
    if (predicate !== undefined && !predicate(query)) continue;
    for (const obs of query.observers) {
      if (isSubscriberQueryFn(obs.options.queryFn)) continue;
      const refetchable = obs as unknown as RefetchableObserver;
      if (refetchable.options.enabled === false) continue;
      void refetchable.refetch(NO_CANCEL);
      break;
    }
  }
}
